import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Eye, Upload, Download, LogOut, Check, X, UserX } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const DEPARTMENTS = ["Finance", "Engineering", "Human Resources", "Marketing", "Operations", "Sales", "IT"];

const TERMINATION_REASONS = [
  "Resignation",
  "End of Contract",
  "Misconduct",
  "Absconding Work",
  "Redundancy",
  "Retirement",
  "Medical Grounds",
  "Mutual Agreement",
  "Gross Misconduct",
  "Other",
];

const defaultForm = {
  name: "", email: "", phone: "", id_number: "", gender: "", kra_pin: "", shif_number: "", nssf_number: "",
  department: "Finance", job_title: "", basic_salary: "", allowances: "0",
  car_benefit: "0", meals_benefit: "0", non_cash_benefits: "0", housing_benefit: "0", other_benefits: "0",
  other_pension: "0", post_retirement_medical: "0", mortgage_interest: "0",
  helb_deduction: "0", is_pwd: false, pwd_exemption: "0",
  bank_name: "", bank_account_name: "", bank_account: "", bank_code: "", bank_branch_name: "", bank_branch_code: "",
  mpesa_phone: "", role: "employee", reports_to: "none", leave_email_recipient: false, status: "active", start_date: "",
};

const InputField = ({ label, name, type = "text", required = false, placeholder = "", form, errors, handleChange }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      data-testid={`employee-${name}-input`}
      type={type}
      value={form[name]}
      onChange={(e) => handleChange(name, e.target.value)}
      className={`bg-white border rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 transition-all text-sm ${
        errors[name]
          ? "border-red-400 focus:ring-red-200 focus:border-red-500"
          : "border-slate-200 focus:ring-[#002FA7]/30 focus:border-[#002FA7]"
      }`}
      placeholder={placeholder}
    />
    {errors[name] && (
      <p className="text-xs text-red-500 mt-1">{errors[name]}</p>
    )}
  </div>
);

// ── Pending Resignations Section ──────────────────────────────────────

function PendingResignationsSection() {
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchResignations = () => {
    API.get("/admin/resignations")
      .then(({ data }) => setResignations(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchResignations();
  }, []);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await API.post(`/admin/resignations/${id}/approve`, {});
      toast.success("Resignation approved successfully.");
      fetchResignations();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Enter rejection reason (optional):");
    setActionLoading(id);
    try {
      await API.post(`/admin/resignations/${id}/reject`, { reason: reason || "" });
      toast.success("Resignation rejected.");
      fetchResignations();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setActionLoading(null);
    }
  };

  const pending = resignations.filter((r) => r.status === "pending");
  const history = resignations.filter((r) => r.status !== "pending");

  return (
    <div className="space-y-6">
      {/* Pending Resignations */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">
          Pending Review ({pending.length})
        </h3>
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Employee</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Department</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden lg:table-cell">Reason</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Notice End</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Submitted</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell>
                </TableRow>
              ) : pending.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No pending resignations</TableCell>
                </TableRow>
              ) : (
                pending.map((r) => (
                  <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <TableCell className="px-4 py-3">
                      <div className="font-medium text-sm text-slate-900">{r.employee_name}</div>
                      <div className="text-xs text-slate-500">{r.employee_email}</div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{r.department || "—"}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-700 hidden lg:table-cell max-w-[200px] truncate" title={r.reason}>
                      {r.reason}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-700">
                      {r.notice_end_date ? new Date(r.notice_end_date).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleApprove(r.id)}
                          disabled={actionLoading === r.id}
                          className="p-1.5 hover:bg-green-50 rounded-sm transition-colors text-slate-500 hover:text-green-600 disabled:opacity-50"
                          title="Approve Resignation"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleReject(r.id)}
                          disabled={actionLoading === r.id}
                          className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600 disabled:opacity-50"
                          title="Reject Resignation"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Resignation History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            History ({history.length})
          </h3>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Employee</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Reason</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Submitted</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Resolved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((r) => (
                  <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <TableCell className="px-4 py-3">
                      <div className="font-medium text-sm text-slate-900">{r.employee_name}</div>
                      <div className="text-xs text-slate-500">{r.employee_email}</div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell max-w-[200px] truncate" title={r.reason}>
                      {r.reason}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <Badge
                        variant={r.status === "approved" ? "default" : "secondary"}
                        className={`text-xs capitalize ${
                          r.status === "approved"
                            ? "bg-green-100 text-green-700 hover:bg-green-100"
                            : "bg-red-100 text-red-700 hover:bg-red-100"
                        }`}
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                      {r.approved_at || r.rejected_at
                        ? new Date(r.approved_at || r.rejected_at).toLocaleDateString()
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────

export default function EmployeesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [activeTab, setActiveTab] = useState("active");

  // Termination dialog state
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [terminateEmployee, setTerminateEmployee] = useState(null);
  const [terminateForm, setTerminateForm] = useState({
    date_of_termination: new Date().toISOString().split("T")[0],
    reason: "",
    notes: "",
  });
  const [terminating, setTerminating] = useState(false);

  const fetchEmployees = () => {
    API.get("/employees")
      .then(({ data }) => setEmployees(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEmployees();
    API.get("/employees/managers").then(({ data }) => setManagers(data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(defaultForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditId(emp.id);
    setErrors({});
    setForm({
      name: emp.name || "", email: emp.email || "", phone: emp.phone || "", gender: emp.gender || "",
      id_number: emp.id_number || "", kra_pin: emp.kra_pin || "",
      shif_number: emp.shif_number || "", nssf_number: emp.nssf_number || "",
      department: emp.department || "Finance", job_title: emp.job_title || "",
      basic_salary: String(emp.basic_salary || ""), allowances: String(emp.allowances || "0"),
      car_benefit: String(emp.car_benefit || "0"), meals_benefit: String(emp.meals_benefit || "0"),
      non_cash_benefits: String(emp.non_cash_benefits || "0"), housing_benefit: String(emp.housing_benefit || "0"),
      other_benefits: String(emp.other_benefits || "0"), other_pension: String(emp.other_pension || "0"),
      post_retirement_medical: String(emp.post_retirement_medical || "0"),
      mortgage_interest: String(emp.mortgage_interest || "0"),
      helb_deduction: String(emp.helb_deduction || "0"),
      is_pwd: emp.is_pwd || false, pwd_exemption: String(emp.pwd_exemption || "0"),
      bank_name: emp.bank_name || "", bank_account_name: emp.bank_account_name || "",
      bank_account: emp.bank_account || "", bank_code: emp.bank_code || "",
      bank_branch_name: emp.bank_branch_name || "", bank_branch_code: emp.bank_branch_code || "",
      mpesa_phone: emp.mpesa_phone || "", role: emp.role || "employee",
      reports_to: emp.reports_to && emp.reports_to !== "" ? emp.reports_to : "none",
      leave_email_recipient: Boolean(emp.leave_email_recipient),
      status: emp.status || "active",
      start_date: emp.start_date || "",
    });
    setDialogOpen(true);
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Full name is required.";
    else if (form.name.trim().length < 2) newErrors.name = "Name must be at least 2 characters.";

    if (!form.email.trim()) newErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      newErrors.email = "Enter a valid email address.";

    if (form.phone && !/^\+?[\d\s\-()]{7,15}$/.test(form.phone.trim()))
      newErrors.phone = "Enter a valid phone number.";

    if (form.kra_pin && !/^[A-Z]\d{9}[A-Z]$/.test(form.kra_pin.trim()))
      newErrors.kra_pin = "KRA PIN format: A001969685Z (letter, 9 digits, letter).";

    if (form.mpesa_phone && !/^\+?2547\d{8}$/.test(form.mpesa_phone.replace(/\s/g, "")))
      newErrors.mpesa_phone = "Enter a valid Safaricom number (e.g. +254712345678).";

    if (!form.department) newErrors.department = "Department is required.";

    if (!form.job_title.trim()) newErrors.job_title = "Job title is required.";

    if (!form.basic_salary && form.basic_salary !== 0)
      newErrors.basic_salary = "Basic salary is required.";
    else if (isNaN(parseFloat(form.basic_salary)) || parseFloat(form.basic_salary) <= 0)
      newErrors.basic_salary = "Basic salary must be a positive number.";

    const numericFields = [
      "allowances", "car_benefit", "meals_benefit", "non_cash_benefits",
      "housing_benefit", "other_benefits", "other_pension",
      "post_retirement_medical", "mortgage_interest", "helb_deduction",
    ];
    numericFields.forEach((field) => {
      const val = parseFloat(form[field]);
      if (isNaN(val) || val < 0) newErrors[field] = "Must be 0 or more.";
    });

    if (form.is_pwd) {
      const exemption = parseFloat(form.pwd_exemption);
      if (isNaN(exemption) || exemption < 0)
        newErrors.pwd_exemption = "Must be 0 or more.";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        reports_to: form.reports_to === "none" ? "" : form.reports_to,
        leave_email_recipient: Boolean(form.leave_email_recipient),
        basic_salary: parseFloat(form.basic_salary) || 0,
        allowances: parseFloat(form.allowances) || 0,
        car_benefit: parseFloat(form.car_benefit) || 0,
        meals_benefit: parseFloat(form.meals_benefit) || 0,
        non_cash_benefits: parseFloat(form.non_cash_benefits) || 0,
        housing_benefit: parseFloat(form.housing_benefit) || 0,
        other_benefits: parseFloat(form.other_benefits) || 0,
        other_pension: parseFloat(form.other_pension) || 0,
        post_retirement_medical: parseFloat(form.post_retirement_medical) || 0,
        mortgage_interest: parseFloat(form.mortgage_interest) || 0,
        helb_deduction: parseFloat(form.helb_deduction) || 0,
        pwd_exemption: parseFloat(form.pwd_exemption) || 0,
      };
      if (editId) {
        await API.put(`/employees/${editId}`, payload);
        toast.success("Employee updated successfully.");
      } else {
        await API.post("/employees", payload);
        toast.success("Employee added successfully.");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this employee? This action cannot be undone.")) return;
    try {
      await API.delete(`/employees/${id}`);
      toast.success("Employee deleted.");
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await API.post("/employees/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message);
      if (data.errors?.length > 0) {
        data.errors.slice(0, 5).forEach((err) => toast.error(err));
      }
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = async (type = "full") => {
    try {
      const response = await API.get(`/employees/csv-template?type=${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `employee_import_template_${type}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Failed to download template.");
    }
  };

  // ── Terminate Employee ──────────────────────────────────────────────

  const openTerminateDialog = (emp) => {
    setTerminateEmployee(emp);
    setTerminateForm({
      date_of_termination: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
    });
    setTerminateDialogOpen(true);
  };

  const handleTerminate = async (e) => {
    e.preventDefault();
    if (!terminateForm.reason) {
      toast.error("Please select a termination reason.");
      return;
    }
    setTerminating(true);
    try {
      await API.post(`/employees/${terminateEmployee.id}/terminate`, terminateForm);
      toast.success(`${terminateEmployee.name} has been terminated.`);
      setTerminateDialogOpen(false);
      setTerminateEmployee(null);
      fetchEmployees();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTerminating(false);
    }
  };

  // ── Filter employees by status ──────────────────────────────────────

  const activeEmployees = employees.filter((e) => e.status === "active");
  const terminatedEmployees = employees.filter((e) => e.status === "inactive");

  const filteredActive = activeEmployees.filter((e) =>
    (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (e.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (e.department || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredTerminated = terminatedEmployees.filter((e) =>
    (e.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (e.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (e.department || "").toLowerCase().includes(search.toLowerCase())
  );

  const isAdminOrHr = user?.role === "admin" || user?.role === "hr_manager";

  // Shared props passed down to every InputField
  const fieldProps = { form, errors, handleChange };

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Employees
        </h1>
        <div className="flex items-center gap-2">
          <Select onValueChange={(value) => downloadTemplate(value)}>
            <SelectTrigger
              data-testid="download-template-btn"
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2 w-40"
            >
              <Download className="h-4 w-4" /> Template
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minimal">Minimal Template</SelectItem>
              <SelectItem value="full">Full Template</SelectItem>
            </SelectContent>
          </Select>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCsvImport}
            className="hidden"
            data-testid="csv-file-input"
          />
          <button
            data-testid="import-csv-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <Upload className="h-4 w-4" /> {importing ? "Importing..." : "Import CSV"}
          </button>
          <button
            data-testid="add-employee-button"
            onClick={openCreate}
            className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Add Employee
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2 max-w-sm">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          data-testid="employee-search-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employees..."
          className="bg-transparent border-none outline-none w-full text-sm text-slate-700"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="border-b border-slate-200 w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger
            value="active"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Active Employees ({activeEmployees.length})
          </TabsTrigger>
          <TabsTrigger
            value="resignations"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Resignations
          </TabsTrigger>
          <TabsTrigger
            value="terminated"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Terminated ({terminatedEmployees.length})
          </TabsTrigger>
        </TabsList>

        {/* ── Active Employees Tab ──────────────────────────────────── */}
        <TabsContent value="active" className="pt-4">
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Name</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Department</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden lg:table-cell">Job Title</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Basic Salary</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Role</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell>
                  </TableRow>
                ) : filteredActive.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No active employees found</TableCell>
                  </TableRow>
                ) : (
                  filteredActive.map((emp) => (
                    <TableRow
                      key={emp.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`employee-row-${emp.id}`}
                    >
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div>
                            <div className="font-medium text-sm text-slate-900">{emp.name}</div>
                            <div className="text-xs text-slate-500">{emp.email}</div>
                          </div>
                          {emp.leave_email_recipient && (
                            <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20">
                              Leave recipient
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{emp.department}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 hidden lg:table-cell">{emp.job_title}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 text-right font-mono">
                        KES {Number(emp.basic_salary).toLocaleString()}
                      </TableCell>
                      <TableCell className="px-4 py-3 hidden sm:table-cell">
                        <Badge variant="outline" className="text-xs capitalize">
                          {(emp.role || "employee").replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <button
                          data-testid={`view-profile-${emp.id}`}
                          onClick={() => navigate(`/employees/${emp.id}`)}
                          className="p-1.5 hover:bg-[#002FA7]/10 rounded-sm transition-colors text-slate-500 hover:text-[#002FA7]"
                          title="View Profile"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`edit-employee-${emp.id}`}
                          onClick={() => openEdit(emp)}
                          className="p-1.5 hover:bg-slate-100 rounded-sm transition-colors text-slate-500 hover:text-slate-900"
                          title="Edit Employee"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        {isAdminOrHr && (
                          <button
                            onClick={() => openTerminateDialog(emp)}
                            className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600 ml-1"
                            title="Terminate Employment"
                          >
                            <UserX className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          data-testid={`delete-employee-${emp.id}`}
                          onClick={() => handleDelete(emp.id)}
                          className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600 ml-1"
                          title="Delete Employee"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ── Resignations Tab ──────────────────────────────────────── */}
        <TabsContent value="resignations" className="pt-4">
          <PendingResignationsSection />
        </TabsContent>

        {/* ── Terminated Employees Tab ──────────────────────────────── */}
        <TabsContent value="terminated" className="pt-4">
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Name</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Department</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden lg:table-cell">Job Title</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Termination Reason</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Terminated On</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell>
                  </TableRow>
                ) : filteredTerminated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">No terminated employees found</TableCell>
                  </TableRow>
                ) : (
                  filteredTerminated.map((emp) => (
                    <TableRow
                      key={emp.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <TableCell className="px-4 py-3">
                        <div className="font-medium text-sm text-slate-900">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.email}</div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{emp.department}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 hidden lg:table-cell">{emp.job_title}</TableCell>
                      <TableCell className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex flex-col gap-1">
                          <Badge variant="secondary" className="text-xs capitalize w-fit">
                            {emp.termination_reason || "N/A"}
                          </Badge>
                          {emp.termination_notes && (
                            <span className="text-[10px] text-slate-500 leading-tight" title={emp.termination_notes}>
                              {emp.termination_notes}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">
                        {emp.date_of_termination ? new Date(emp.date_of_termination).toLocaleDateString() : "N/A"}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <button
                          onClick={() => navigate(`/employees/${emp.id}`)}
                          className="p-1.5 hover:bg-[#002FA7]/10 rounded-sm transition-colors text-slate-500 hover:text-[#002FA7]"
                          title="View Profile"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Add / Edit Employee Dialog ──────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Employee" : "Add Employee"}</DialogTitle>
            <DialogDescription>
              {editId ? "Update employee information below." : "Fill in the details to add a new employee."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} data-testid="employee-form" noValidate>
            <div className="space-y-5 py-4">

              {/* Personal Information */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3 border-b border-slate-100 pb-2">
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField label="Full Name" name="name" required placeholder="John Doe" {...fieldProps} />
                  <InputField label="Email" name="email" type="email" required placeholder="john@company.com" {...fieldProps} />
                  <InputField label="Phone" name="phone" placeholder="+254 700 000 000" {...fieldProps} />
                  <InputField label="National ID" name="id_number" placeholder="National ID number" {...fieldProps} />
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Gender</label>
                    <Select value={form.gender} onValueChange={(v) => handleChange("gender", v)}>
                      <SelectTrigger data-testid="employee-gender-select" className="h-9">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other / Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <InputField label="KRA PIN" name="kra_pin" placeholder="e.g. A001969685Z" {...fieldProps} />
                  <InputField label="SHIF Number" name="shif_number" placeholder="SHIF number" {...fieldProps} />
                  <InputField label="NSSF Number" name="nssf_number" placeholder="NSSF number" {...fieldProps} />
                  <InputField label="M-Pesa Phone" name="mpesa_phone" placeholder="+254712345678" {...fieldProps} />
                </div>
              </section>

              {/* Employment Details */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3 border-b border-slate-100 pb-2">
                  Employment Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <Select value={form.department} onValueChange={(v) => handleChange("department", v)}>
                      <SelectTrigger data-testid="employee-department-select" className={`h-9 ${errors.department ? "border-red-400" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department}</p>}
                  </div>
                  <InputField label="Job Title / Position" name="job_title" required placeholder="Software Developer" {...fieldProps} />
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Role</label>
                    <Select value={form.role} onValueChange={(v) => handleChange("role", v)}>
                      <SelectTrigger data-testid="employee-role-select" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="hr_manager">HR Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Reports To (Manager)</label>
                    <Select value={form.reports_to} onValueChange={(v) => handleChange("reports_to", v)}>
                      <SelectTrigger data-testid="employee-reports-to-select" className="h-9">
                        <SelectValue placeholder="Select manager" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {managers.filter((m) => m.id !== editId).map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name} — {m.job_title || m.department}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Status</label>
                    <Select value={form.status} onValueChange={(v) => handleChange("status", v)}>
                      <SelectTrigger data-testid="employee-status-select" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <InputField label="Date of Employment" name="start_date" type="date" {...fieldProps} />
                  <div className="flex items-center gap-3 pt-5">
                    <input
                      type="checkbox"
                      id="leave-email-recipient"
                      checked={form.leave_email_recipient}
                      onChange={(e) => handleChange("leave_email_recipient", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                    />
                    <label htmlFor="leave-email-recipient" className="text-sm text-slate-700">Leave email recipient</label>
                  </div>
                  <div className="flex items-center gap-3 pt-5">
                    <input
                      type="checkbox"
                      id="is_pwd"
                      checked={form.is_pwd}
                      onChange={(e) => handleChange("is_pwd", e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                    />
                    <label htmlFor="is_pwd" className="text-sm text-slate-700">Person with Disability (PWD)</label>
                  </div>
                  {form.is_pwd && (
                    <InputField label="PWD Exemption Amount" name="pwd_exemption" type="number" placeholder="0" {...fieldProps} />
                  )}
                </div>
              </section>

              {/* Salary & Benefits */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3 border-b border-slate-100 pb-2">
                  Salary & Benefits
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField label="Basic Salary (KES)" name="basic_salary" type="number" required placeholder="0" {...fieldProps} />
                  <InputField label="Allowances" name="allowances" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Car Benefit" name="car_benefit" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Meals Benefit" name="meals_benefit" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Non-Cash Benefits" name="non_cash_benefits" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Housing Benefit" name="housing_benefit" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Other Benefits" name="other_benefits" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Other Pension" name="other_pension" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Post-Retirement Medical" name="post_retirement_medical" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="Mortgage Interest" name="mortgage_interest" type="number" placeholder="0" {...fieldProps} />
                  <InputField label="HELB Deduction" name="helb_deduction" type="number" placeholder="0" {...fieldProps} />
                </div>
              </section>

              {/* Bank Details */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3 border-b border-slate-100 pb-2">
                  Bank Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField label="Bank Name" name="bank_name" placeholder="e.g. Equity Bank" {...fieldProps} />
                  <InputField label="Account Name" name="bank_account_name" placeholder="Account holder name" {...fieldProps} />
                  <InputField label="Account Number" name="bank_account" placeholder="Account number" {...fieldProps} />
                  <InputField label="Bank Code" name="bank_code" placeholder="e.g. 68" {...fieldProps} />
                  <InputField label="Branch Name" name="bank_branch_name" placeholder="Branch name" {...fieldProps} />
                  <InputField label="Branch Code" name="bank_branch_code" placeholder="Branch code" {...fieldProps} />
                </div>
              </section>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-6 py-2.5 font-medium transition-colors text-sm disabled:opacity-50"
              >
                {submitting ? "Saving..." : editId ? "Update Employee" : "Add Employee"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Terminate Employee Dialog ───────────────────────────────── */}
      <Dialog open={terminateDialogOpen} onOpenChange={(open) => { setTerminateDialogOpen(open); if (!open) setTerminateEmployee(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Terminate Employment</DialogTitle>
            <DialogDescription>
              {terminateEmployee ? (
                <>This will set <strong>{terminateEmployee.name}</strong> as inactive and record the termination details.</>
              ) : (
                <>Fill in the details to terminate an employee.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTerminate}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                  Date of Termination <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={terminateForm.date_of_termination}
                  onChange={(e) => setTerminateForm((prev) => ({ ...prev, date_of_termination: e.target.value }))}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] text-sm"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                  Reason <span className="text-red-500">*</span>
                </label>
                <Select
                  value={terminateForm.reason}
                  onValueChange={(v) => setTerminateForm((prev) => ({ ...prev, reason: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select termination reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {TERMINATION_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  value={terminateForm.notes}
                  onChange={(e) => setTerminateForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] text-sm min-h-[80px]"
                  placeholder="Additional notes about the termination..."
                />
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setTerminateDialogOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={terminating || !terminateForm.reason}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-6 py-2.5 font-medium transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
              >
                <UserX className="h-4 w-4" />
                {terminating ? "Terminating..." : "Terminate Employment"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
